import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import prune from 'underscore.string/prune';
import SiteContext from './SiteContext';
import onRouteChangeFinished from '../lib/onRouteChangeFinished';

const maxMetaDescriptionLength = 160;
const truncateString = '...';
const pruneLength = maxMetaDescriptionLength - truncateString.length;

const getCanonicalUrl = (url) => {
  const parsedUrl = new URL(url);
  parsedUrl.search = '';

  return `${parsedUrl}`;
};

const Head = ({
  title,
  description,
  imageUrl,
  imageDimensions,
  createdAt,
  author,
  robotsMeta,
  jsonLdData,
  addCanonical,
  children,
  prelude,
}) => {
  const { origin } = useContext(SiteContext);
  const router = useRouter();

  const url = `${new URL(router.asPath, origin)}`;

  useEffect(() => {
    if (title) {
      onRouteChangeFinished();
    }
  }, [url, title]);

  const truncatedDescription = prune(description, pruneLength);
  const canonicalUrl = getCanonicalUrl(url);

  return (
    <NextHead>
      <title key="title">{title}</title>
      {prelude}
      {robotsMeta && <meta name="robots" content={robotsMeta} />}
      <meta property="og:site_name" content="FashionUnited" />
      <meta name="twitter:site" content="@FashionUnited" />
      <meta
        name="og:title"
        key="og:title"
        property="og:title"
        content={title}
      />
      <meta name="twitter:title" key="twitter:title" content={title} />
      <meta name="description" content={truncatedDescription} />
      <meta property="og:description" content={truncatedDescription} />
      <meta name="twitter:description" content={truncatedDescription} />
      {author
        && (
          <>
            <meta property="og:type" content="article" />
            <meta property="article:author" content={author} />
            <meta name="twitter:creator" content={author} />
          </>
        )}
      {url && <meta property="og:url" content={url} />}
      {createdAt && <meta property="DC.date.issued" content={createdAt} />}
      {imageUrl
        && (
          <>
            <meta property="og:image" content={imageUrl} />
            <meta
              property="og:image:secure_url"
              content={imageUrl}
            />
            {imageDimensions && imageDimensions.width && (
              <meta
                property="og:image:width"
                content={imageDimensions.width}
              />
            )}
            {imageDimensions && imageDimensions.height && (
              <meta
                property="og:image:height"
                content={imageDimensions.height}
              />
            )}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image:alt" content={title} />
            <meta name="twitter:image:src" content={imageUrl} />
          </>
        )}
      {addCanonical && <link rel="canonical" href={canonicalUrl} />}
      {jsonLdData
        && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: jsonLdData,
            }}
          />
        )}
      {children}
    </NextHead>
  );
};

Head.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  imageDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  createdAt: PropTypes.string,
  author: PropTypes.string,
  robotsMeta: PropTypes.string,
  jsonLdData: PropTypes.string,
  addCanonical: PropTypes.bool,
  children: PropTypes.node,
  prelude: PropTypes.node,
};

Head.defaultProps = {
  imageUrl: null,
  imageDimensions: null,
  createdAt: null,
  author: null,
  robotsMeta: null,
  jsonLdData: null,
  addCanonical: true,
  children: null,
  prelude: null,
};

export default Head;
